if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.stickyBar = this;
        this.sectionId = this.dataset.sectionId;
        this.dynamicCheckoutEnabled = this.dataset.dynamicCheckout === 'true';
        this.buyNowMin = parseInt(this.dataset.buyNowMin, 10) || 0;
        this.buyNowPrice = parseInt(this.dataset.buyNowPrice, 10) || 0;
        this.mainBuyButtons = null;
        this.mainSubmitButton = null;
        this.mainVariantInput = null;
        this.form = this.querySelector('form');
        this.addButton = this.querySelector('.sticky-add-to-cart__button--add');
        this.buyButton = this.querySelector('.sticky-add-to-cart__button--buy');
        this.addButtonLabel = this.querySelector('[data-sticky-add-label]');
        this.priceContainer = this.querySelector('.sticky-add-to-cart__price');
        this.variantInput = this.querySelector('input[name="id"]');
        this.observer = null;
        this.mainButtonObserver = null;
        this.variantChangeUnsubscriber = null;
        this.pendingVisibilityFrame = null;
        this.isVisible = false;
        this.defaultAddLabel = this.addButton?.dataset.defaultLabel || 'Add to cart';
        this.printLabel = this.addButton?.dataset.ctaLabelPrint || 'Add Print to Cart';
        this.frameLabel = this.addButton?.dataset.ctaLabelFrame || 'Add Frame to Cart';
        this.productSystem = this.addButton?.dataset.productSystem || 'default';
        this.soldOutLabel = this.addButton?.dataset.soldOutLabel || 'Sold out';
        this.unavailableLabel = this.addButton?.dataset.unavailableLabel || 'Unavailable';
        this.onMainVariantInputChange = this.onMainVariantInputChange.bind(this);
        this.onQuantityChange = this.syncBuyNowVisibility.bind(this);
        this.quantityUpdateUnsubscriber = null;
      }

      connectedCallback() {
        this.initObserver();
        this.bindEvents();
        this.bindMainFormSync();
        this.subscribeToVariantChanges();
        this.syncButtonStateFromMain();
        this.syncBuyNowVisibility();
        document.addEventListener('change', this.onQuantityChange);
        document.addEventListener('input', this.onQuantityChange);
        if (typeof subscribe === 'function') {
          this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.onQuantityChange);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('change', this.onQuantityChange);
        document.removeEventListener('input', this.onQuantityChange);
        this.quantityUpdateUnsubscriber?.();

        if (this.observer) {
          this.observer.disconnect();
        }

        if (this.mainButtonObserver) {
          this.mainButtonObserver.disconnect();
        }

        if (this.mainVariantInput) {
          this.mainVariantInput.removeEventListener('change', this.onMainVariantInputChange);
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.pendingVisibilityFrame) {
          window.cancelAnimationFrame(this.pendingVisibilityFrame);
        }
      }

      initObserver() {
        // Find the main buy buttons section to observe
        this.mainBuyButtons = document.querySelector('.product-form__buttons');
        
        if (!this.mainBuyButtons) return;

        const options = {
          root: null,
          rootMargin: '0px',
          threshold: 0
        };

        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.hide();
            } else {
              // Only show if we've scrolled past (not above)
              const rect = this.mainBuyButtons.getBoundingClientRect();
              if (rect.top < 0) {
                this.show();
              } else {
                this.hide();
              }
            }
          });
        }, options);

        this.observer.observe(this.mainBuyButtons);
      }

      bindEvents() {
        // Add to Cart button
        if (this.form && this.addButton) {
          this.form.addEventListener('submit', this.onAddToCart.bind(this));
        }

        // Buy Now button
        if (this.buyButton) {
          this.buyButton.addEventListener('click', this.onBuyNow.bind(this));
        }
      }

      bindMainFormSync() {
        const mainForm = this.sectionId ? document.getElementById(`product-form-${this.sectionId}`) : null;
        this.mainSubmitButton =
          (this.sectionId && document.getElementById(`ProductSubmitButton-${this.sectionId}`)) ||
          document.querySelector('.product-form__submit');
        this.mainVariantInput = mainForm?.querySelector('input[name="id"]') || document.querySelector('.product-form input[name="id"]');
        this.syncProductSystemFromMain();

        if (this.mainVariantInput) {
          this.syncVariantIdFromMain();
          this.mainVariantInput.addEventListener('change', this.onMainVariantInputChange);
        }

        if (!this.mainSubmitButton || typeof MutationObserver !== 'function') return;

        this.mainButtonObserver = new MutationObserver(() => {
          this.syncProductSystemFromMain();
          this.syncButtonStateFromMain();
        });
        this.mainButtonObserver.observe(this.mainSubmitButton, {
          attributes: true,
          childList: true,
          subtree: true,
        });
      }

      subscribeToVariantChanges() {
        // Listen for variant changes from the main product form
        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            this.updateVariant(event.data);
          });
        }
      }

      updateVariant(data) {
        if (!data || !data.variant) {
          this.setUnavailableState();
          return;
        }

        const variant = data.variant;
        
        // Update variant ID
        if (this.variantInput) {
          this.variantInput.value = variant.id;
          this.variantInput.disabled = !variant.available;
        }

        // Update button state
        const label = variant.available ? this.getMainSubmitLabel() : this.soldOutLabel;
        this.setButtonState(!variant.available, label);

        // Update price
        this.updatePrice(variant);
        this.updateTrustLine(data.html);
        // Fail closed: a variant with no readable price never shows Buy Now.
        this.buyNowPrice = parseInt(variant.price, 10) || 0;
        this.syncButtonStateFromMain();
        this.syncBuyNowVisibility();
      }

      // The benefit line depends on the variant (frames: prepaid only, flat delivery). Copy the server-rendered
      // line from the section HTML that product-info.js already fetched for this variant.
      updateTrustLine(html) {
        if (!html || !this.sectionId) return;
        const id = `sticky-add-to-cart-trust-${this.sectionId}`;
        const source = html.getElementById(id);
        const destination = document.getElementById(id);
        if (source && destination) destination.innerHTML = source.innerHTML;
      }

      updatePrice(variant) {
        if (!this.priceContainer) return;

        let priceHtml = '';
        
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceHtml = `
            <span class="price-item price-item--sale">${this.formatMoney(variant.price)}</span>
            <span class="price-item price-item--regular price-item--last">${this.formatMoney(variant.compare_at_price)}</span>
          `;
        } else {
          priceHtml = `<span class="price-item price-item--regular">${this.formatMoney(variant.price)}</span>`;
        }

        this.priceContainer.innerHTML = priceHtml;
      }

      onMainVariantInputChange() {
        this.syncVariantIdFromMain();
        this.syncButtonStateFromMain();
      }

      syncVariantIdFromMain() {
        if (!this.mainVariantInput || !this.variantInput) return;

        this.variantInput.value = this.mainVariantInput.value || '';
      }

      syncProductSystemFromMain() {
        const productInfo = this.closest('product-info') || document;
        const pickerSystem = productInfo.querySelector('variant-selects')?.dataset.productSystem;
        const mainButtonSystem = this.mainSubmitButton?.dataset.productSystem;

        this.productSystem =
          pickerSystem || mainButtonSystem || this.addButton?.dataset.productSystem || this.productSystem || 'default';
      }

      syncButtonStateFromMain() {
        this.syncVariantIdFromMain();

        const mainButtonDisabled =
          this.mainSubmitButton?.disabled || this.mainSubmitButton?.getAttribute('aria-disabled') === 'true';
        const hasVariant = Boolean(this.variantInput?.value);
        const shouldDisable = Boolean(mainButtonDisabled || !hasVariant);
        const fallbackLabel = hasVariant ? this.defaultAddLabel : this.unavailableLabel;

        this.setButtonState(shouldDisable, this.getMainSubmitLabel() || fallbackLabel);
      }

      syncBuyNowVisibility() {
        if (!this.buyButton) return;

        const mainPaymentButton = document.querySelector('.shopify-payment-button');
        const shouldShowBuyNow = this.dynamicCheckoutEnabled && Boolean(mainPaymentButton) && this.meetsBuyNowMinimum();

        this.buyButton.hidden = !shouldShowBuyNow;
        if (!shouldShowBuyNow) {
          this.buyButton.disabled = true;
          this.buyButton.setAttribute('aria-disabled', 'true');
        } else {
          const disabled = Boolean(this.addButton?.disabled);
          this.buyButton.disabled = disabled;
          this.buyButton.toggleAttribute('aria-disabled', disabled);
        }
      }

      getBuyNowQuantity() {
        const quantityInput =
          document.querySelector(`.quantity__input[form="product-form-${this.sectionId}"]`) ||
          document.querySelector('.product-form input[name="quantity"]');

        return quantityInput ? parseInt(quantityInput.value, 10) || 0 : 1;
      }

      // Buy Now goes straight to checkout, so it needs the cart minimum met by price x quantity.
      meetsBuyNowMinimum() {
        return this.buyNowPrice * this.getBuyNowQuantity() >= this.buyNowMin;
      }

      getMainSubmitLabel() {
        const mainLabel = this.mainSubmitButton?.querySelector('span')?.textContent?.trim();
        const stickyLabel = this.addButtonLabel?.textContent?.trim();

        return mainLabel || stickyLabel || this.defaultAddLabel;
      }

      getCtaLabelVariant(label = this.getMainSubmitLabel()) {
        if (label === this.frameLabel) return 'single_frame';
        if (this.productSystem === 'single' || label === this.printLabel) return 'single_print';
        if (this.productSystem === 'kit') return 'kit';

        return 'default';
      }

      getSelectedOptionValues() {
        const productInfo = document.querySelector('product-info') || document;
        const selectedValues = [
          ...Array.from(productInfo.querySelectorAll('variant-selects select')).map(
            (select) => select.selectedOptions?.[0]?.textContent?.trim() || select.value
          ),
          ...Array.from(productInfo.querySelectorAll('variant-selects input[type="radio"]:checked')).map(
            (input) => input.value
          ),
        ];

        return selectedValues.filter(Boolean);
      }

      getCtaContext(formData) {
        this.syncProductSystemFromMain();

        const label = this.getMainSubmitLabel();

        return {
          source: 'sticky-add-to-cart',
          cta_label: label,
          cta_label_variant: this.getCtaLabelVariant(label),
          product_system: this.productSystem,
          product_type: this.addButton?.dataset.productType || this.productSystem,
          product_id: this.addButton?.dataset.productId || '',
          product_handle: this.addButton?.dataset.productHandle || '',
          template_type: this.addButton?.dataset.templateType || window.ArtTheoryAnalytics?.context?.template_type || '',
          collection_handle: this.addButton?.dataset.collectionHandle || window.ArtTheoryAnalytics?.context?.collection_handle || '',
          variant_id: formData.get('id'),
          variant_options: this.getSelectedOptionValues(),
        };
      }

      trackCtaAdd(context) {
        try {
          if (window.ArtTheoryAnalytics && typeof window.ArtTheoryAnalytics.track === 'function') {
            window.ArtTheoryAnalytics.track('add_to_cart', context);
            return;
          }

          if (Array.isArray(window.dataLayer)) {
            window.dataLayer.push({ event: 'add_to_cart', ...context });
          }
        } catch (error) {
          console.warn('CTA analytics tracking failed', error);
        }
      }

      setButtonState(disabled, label) {
        if (this.addButton) {
          this.addButton.disabled = disabled;
          this.addButton.toggleAttribute('aria-disabled', disabled);
        }

        if (this.addButtonLabel && label) {
          this.addButtonLabel.textContent = label;
        }

        if (this.buyButton && !this.buyButton.hidden) {
          this.buyButton.disabled = disabled;
          this.buyButton.toggleAttribute('aria-disabled', disabled);
        }

        if (this.variantInput) {
          this.variantInput.disabled = disabled;
        }
      }

      setUnavailableState() {
        if (this.variantInput) {
          this.variantInput.value = '';
          this.variantInput.disabled = true;
        }

        this.setButtonState(true, this.unavailableLabel);
      }

      formatMoney(cents) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          return Shopify.formatMoney(cents, window.theme?.moneyFormat || '₹{{amount_no_decimals}}');
        }
        return `₹${Math.round(cents / 100).toLocaleString('en-IN')}`;
      }

      show() {
        if (this.isVisible) return;

        this.isVisible = true;
        if (this.pendingVisibilityFrame) {
          window.cancelAnimationFrame(this.pendingVisibilityFrame);
        }

        this.pendingVisibilityFrame = window.requestAnimationFrame(() => {
          this.classList.add('is-visible');
          this.pendingVisibilityFrame = null;
        });
      }

      hide() {
        this.isVisible = false;
        if (this.pendingVisibilityFrame) {
          window.cancelAnimationFrame(this.pendingVisibilityFrame);
          this.pendingVisibilityFrame = null;
        }

        this.classList.remove('is-visible');
      }

      onAddToCart(evt) {
        evt.preventDefault();
        
        if (this.addButton.disabled || this.addButton.getAttribute('aria-disabled') === 'true') return;

        this.addButton.classList.add('loading');
        this.addButton.setAttribute('aria-disabled', 'true');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const ctaContext = this.getCtaContext(formData);
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        
        if (cart) {
          formData.append(
            'sections',
            cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(document.activeElement);
        }
        
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              // Error occurred
              console.error('Add to cart error:', response.description);
              return;
            }

            if (!cart) {
              this.trackCtaAdd(ctaContext);
              window.location = window.routes.cart_url;
              return;
            }

            this.trackCtaAdd(ctaContext);

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'sticky-add-to-cart',
              productVariantId: formData.get('id'),
              cartData: response,
              ctaLabel: ctaContext.cta_label,
              ctaLabelVariant: ctaContext.cta_label_variant,
              ctaContext,
            });

            cart.renderContents(response);
          })
          .catch((error) => {
            console.error('Add to cart error:', error);
          })
          .finally(() => {
            this.addButton.classList.remove('loading');
            this.addButton.removeAttribute('aria-disabled');
          });
      }

      onBuyNow(evt) {
        evt.preventDefault();
        
        if (this.buyButton.disabled || this.buyButton.hidden) return;

        const variantId = this.variantInput?.value;
        if (!variantId) return;

        // Get quantity from main form or default to 1
        if (!this.meetsBuyNowMinimum()) return;
        const quantity = this.getBuyNowQuantity();
        const beginCheckoutContext = this.getCtaContext(new FormData(this.form));

        if (window.ArtTheoryAnalytics && typeof window.ArtTheoryAnalytics.track === 'function') {
          window.ArtTheoryAnalytics.track('begin_checkout', {
            ...beginCheckoutContext,
            source: 'sticky-buy-now',
            quantity,
          });
        }

        // Redirect to checkout
        window.location.href = `/cart/${variantId}:${quantity}?checkout`;
      }
    }
  );
}
